import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * The "Issue tax invoice" switch on a PIN operator's subscribers -- radaccess
 * /access/operator/{ns}/invoicepref (feat/pin-provider-types, owner
 * 2026-09-19), RBAC resource `invoicepref` (superadmin and Operator).
 *
 * A PIN operator's subscriber gets a tax invoice ONLY with the switch on, and
 * that invoice is in the upstream (parent) operator's name. radbilling reads
 * the switch and treats "never set" as off.
 */
export interface SubscriberInvoicePref {
  accountid: string;
  issuetaxinvoice: boolean;
  setby: string;
  updatedat: string;
}

/** A subscriber the switch can be set on (radius.registration, named columns only). */
export interface InvoicePrefSubscriber {
  accountid: string;
  username: string;
  name: string;
  mobile: string;
  /** Registration status, "enable" or "disable" (radaccess 2026-10-14; absent from older radaccess). */
  status?: string;
  /** Plan of the latest-ending active device, "" when none (radaccess v1.195; absent before). */
  plan?: string;
  /** When that plan ends, IST "YYYY-MM-DD HH:MM", "" when none (radaccess v1.195; absent before). */
  expiry?: string;
}

/**
 * The switch route carries the subscriber list itself: the billing-accounts
 * list needs billing:read, which is superadmin-only (owner report 2026-09-19,
 * "Permission denied for billing:read").
 */
export interface InvoicePrefs {
  /** False for any operator that is not of type PIN: the switch does nothing there. */
  applies: boolean;
  prefs: SubscriberInvoicePref[];
  /** Only when applies; empty otherwise. */
  subscribers: InvoicePrefSubscriber[];
}

const base = (ns: string) => `/operator/${encodeURIComponent(ns)}/invoicepref`;

export async function getInvoicePrefs(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<InvoicePrefs> {
  const r = await callGateway<InvoicePrefs | null>(identity, config().gatewayBase, base(ns), signal ? { signal } : {});
  return { applies: Boolean(r.data?.applies), prefs: r.data?.prefs ?? [], subscribers: r.data?.subscribers ?? [] };
}

export async function setInvoicePref(identity: CallerIdentity, ns: string, accountId: string, on: boolean): Promise<SubscriberInvoicePref> {
  const r = await callGateway<SubscriberInvoicePref>(identity, config().gatewayBase, `${base(ns)}/${encodeURIComponent(accountId)}`, {
    method: "PUT",
    body: { issuetaxinvoice: on },
  });
  return r.data;
}

/** Digits only, without a leading 91 on a 12-digit number: "+91 98202-17705" -> "9820217705". */
export function mobileDigits(s: string): string {
  const d = s.replace(/\D/g, "");
  return d.length === 12 && d.startsWith("91") ? d.slice(2) : d;
}

/**
 * Subscriber search, mobile first (owner 2026-09-19): digits in the query are
 * matched against the mobile however it was typed; otherwise name, user name
 * or account id.
 */
export function matchesSubscriber(s: InvoicePrefSubscriber, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const digits = mobileDigits(q);
  if (digits.length >= 3 && mobileDigits(s.mobile).includes(digits)) return true;
  return [s.name, s.username, s.accountid].some((f) => f.toLowerCase().includes(q));
}

/** accountid -> switch; a subscriber with no row is OFF, as radbilling reads it. */
export function switchesByAccount(prefs: SubscriberInvoicePref[]): Map<string, boolean> {
  return new Map(prefs.map((p) => [p.accountid.toLowerCase(), p.issuetaxinvoice]));
}
